import { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Typography, Spin, Alert } from 'antd';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useGetAllBranches } from "@/shared/api/client/company-resource.ts";

const { Title } = Typography;
const { Option } = Select;

const LoginFormView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    
    const {
        data: branches,
        isLoading: isBranchesLoading,
        isError: isBranchesError,
        error: branchesError
    } = useGetAllBranches({
        query: {
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            refetchOnWindowFocus: false,
        }
    });
    
    // Check for error or logout params in URL
    useEffect(() => {
        const error = searchParams.get('error');
        const logout = searchParams.get('logout');
        
        if (error === 'true') {
            setLoginError('Invalid username or password. Please try again.');
        } else if (logout) {
            // Could show a success message for logout
            setLoginError(null);
        }
    }, [searchParams]);

    if (isBranchesLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (isBranchesError) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Alert
                    message="Error"
                    description={(branchesError as Error)?.message || 'Failed to load branches'}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!branches?.length) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Alert
                    message="No branches available"
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    const onFinish = (values: any) => {
        setIsSubmitting(true);
        setLoginError(null);
        
        // Handle form submission
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/api/auth/login';
        
        // Add form fields
        Object.keys(values).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = values[key];
            form.appendChild(input);
        });
        
        // Add branch if only one branch
        if (branches.length === 1) {
            const branchInput = document.createElement('input');
            branchInput.type = 'hidden';
            branchInput.name = 'branch';
            branchInput.value = branches[0].branch;
            form.appendChild(branchInput);
        }
        
        document.body.appendChild(form);
        form.submit();
    };

    return (
        <div style={{ width: '100%', maxWidth: 420, padding: '24px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0, fontWeight: 500 }}>Sign In</Title>
            </div>

            {loginError && (
                <Alert
                    message="Login Failed"
                    description={loginError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setLoginError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                name="login"
                onFinish={onFinish}
                layout="vertical"
                size="middle"
            >
                {/* TODO: Add CSRF token when utils are migrated */}

                {branches.length > 1 && (
                    <Form.Item
                        label="Branch"
                        name="branch"
                        rules={[{ required: true, message: 'Please select your branch!' }]}
                    >
                        <Select placeholder="Select your branch" disabled={isSubmitting}>
                            {branches.map((branch) => (
                                <Option key={branch.branch} value={branch.branch}>
                                    {branch.branchName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    label="Username"
                    name="username"
                    rules={[{ required: true, message: 'Please input your username!' }]}
                >
                    <Input 
                        prefix={<UserOutlined />}
                        placeholder="Username" 
                        disabled={isSubmitting}
                    />
                </Form.Item>

                <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: 'Please input your password!' }]}
                >
                    <Input.Password 
                        prefix={<LockOutlined />}
                        placeholder="Password" 
                        disabled={isSubmitting}
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={isSubmitting}
                        style={{ width: '100%' }}
                    >
                        {isSubmitting ? 'Signing In...' : 'Sign In'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default LoginFormView;